import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  wizardBlockCardClassName,
  wizardBlockShellClassName,
} from '../wizardBlockChrome';
import { ImageUploader } from '@/components/common/ImageUploader';
import { FieldHelp } from '@/features/guided-help';
import { uploadImage } from '@/api/tournament/tournamentApi';
import { toast } from '@/components/ui/use-toast';
import { handleGlobalError } from '@/utils/error-handler';

export interface BasicInfoBannerFieldProps {
  bannerUrl: string | null;
  onBannerUrlChange: (url: string) => void;
  /** Shown below the uploader when `wrapInCard` is true. */
  recommendedSize?: string;
  wrapInCard?: boolean;
  altText?: string;
  /** Fires when an upload starts or finishes (success or error). */
  onUploadingChange?: (isUploading: boolean) => void;
}

export function BasicInfoBannerField({
  bannerUrl,
  onBannerUrlChange,
  recommendedSize = '1200x400px',
  wrapInCard = true,
  altText = 'Banner del torneo',
  onUploadingChange,
}: BasicInfoBannerFieldProps) {
  const [isUploading, setIsUploading] = useState(false);

  const setUploading = (value: boolean) => {
    setIsUploading(value);
    onUploadingChange?.(value);
  };

  const handleBannerUpload = async (file: File) => {
    setUploading(true);
    try {
      const result = await uploadImage(file);
      if (result) {
        onBannerUrlChange(result);
        toast({
          title: 'Imagen subida',
          description: 'El banner se ha subido exitosamente.',
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      handleGlobalError(
        error,
        'Error',
        'No se pudo subir la imagen. Intenta nuevamente.',
      );
    } finally {
      setUploading(false);
    }
  };

  const uploader = (
    <>
      <ImageUploader
        onChange={handleBannerUpload}
        isUploading={isUploading}
        imagePreview={bannerUrl}
        altText={altText}
      />
      {wrapInCard && (
        <p className="mt-2 text-xs text-gray-500">
          Recomendado: {recommendedSize}. Formatos: JPG, PNG, WebP.
        </p>
      )}
    </>
  );

  if (!wrapInCard) {
    return (
      <div
        className={cn(wizardBlockShellClassName, 'space-y-2')}
        data-guide-id="wizard.basicInfo.banner"
      >
        {uploader}
      </div>
    );
  }

  return (
    <Card
      className={wizardBlockCardClassName}
      data-guide-id="wizard.basicInfo.banner"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          Banner del torneo
          <FieldHelp topicId="wizard.basicInfo.banner" />
        </CardTitle>
      </CardHeader>
      <CardContent>{uploader}</CardContent>
    </Card>
  );
}
